import type { MenuItemsProps } from "@headlessui/react";
import { Menu as HeadlessMenu, MenuButton, MenuItems, Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type React from "react";
import { Fragment, useEffect, useRef } from "react";
import { classNames } from "../../utils";

export const menuStyles = cva(
	["mt-2 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/10 focus:outline-hidden"],
	{
		variants: {
			size: {
				md: "w-72 p-3",
				custom: "",
			},
		},
		defaultVariants: {
			size: "md",
		},
	},
);

type MenuProps = {
	trigger: (({ open }: { open: boolean }) => React.ReactNode) | React.ReactNode;
	children: (({ open, close }: { open: boolean; close: () => void }) => React.ReactNode) | React.ReactNode;
	size?: VariantProps<typeof menuStyles>["size"];
	className?: string;
	containerClassName?: string;
	disabled?: boolean;
	anchor?: MenuItemsProps["anchor"];
	modal?: boolean;
	style?: React.CSSProperties;
	behavior?: "menu" | "popover";
};

type MenuBodyProps = {
	open: boolean;
	close: () => void;
	containerRef: React.RefObject<HTMLDivElement | null>;
	itemsRef: React.MutableRefObject<HTMLElement | null>;
	trigger: MenuProps["trigger"];
	disabled: boolean;
	modal: boolean;
	anchor: MenuItemsProps["anchor"];
	size?: VariantProps<typeof menuStyles>["size"];
	className?: string;
	children: MenuProps["children"];
};

const MenuBody = ({
	open,
	close,
	containerRef,
	itemsRef,
	trigger,
	disabled,
	modal,
	anchor,
	size,
	className,
	children,
}: MenuBodyProps) => {
	useEffect(() => {
		if (!open) {
			return;
		}

		const onDocumentClickCapture = (event: MouseEvent) => {
			const menuContainer = containerRef.current;
			const menuItems = itemsRef.current;
			const target = event.target as HTMLElement;

			if (
				!menuContainer ||
				menuContainer.contains(target) ||
				menuItems?.contains(target) ||
				target.closest?.("[role='menu']")
			) {
				return;
			}

			close();
		};

		document.addEventListener("click", onDocumentClickCapture, true);

		return () => {
			document.removeEventListener("click", onDocumentClickCapture, true);
		};
	}, [open, close, containerRef, itemsRef]);

	return (
		<>
			<MenuButton as={Fragment} disabled={disabled}>
				{typeof trigger === "function" ? trigger({ open }) : trigger}
			</MenuButton>

			<MenuItems
				ref={(node) => {
					itemsRef.current = node;
				}}
				modal={modal}
				className={classNames("z-50 mt-2", menuStyles({ size }), className)}
				anchor={anchor}
			>
				{typeof children === "function" ? children({ open, close }) : children}
			</MenuItems>
		</>
	);
};

export const Menu = ({
	children,
	trigger,
	size,
	className,
	containerClassName,
	disabled = false,
	modal = false,
	anchor = "bottom start",
	style,
	behavior = "menu",
}: MenuProps) => {
	if (behavior === "popover") {
		return (
			<Popover
				as="div"
				className={classNames("relative", containerClassName)}
				style={{ paddingRight: 0, ...style }}
			>
				{({ open, close }) => (
					<>
						<PopoverButton as={Fragment} disabled={disabled}>
							{typeof trigger === "function" ? trigger({ open }) : trigger}
						</PopoverButton>

						<PopoverPanel
							anchor={anchor}
							className={classNames("z-50 mt-2", menuStyles({ size }), className)}
						>
							{typeof children === "function" ? children({ open, close }) : children}
						</PopoverPanel>
					</>
				)}
			</Popover>
		);
	}

	const containerRef = useRef<HTMLDivElement>(null);
	const itemsRef = useRef<HTMLElement | null>(null);

	return (
		<HeadlessMenu
			as="div"
			ref={containerRef}
			className={classNames("relative", containerClassName)}
			style={{ paddingRight: 0, ...style }}
		>
			{({ open, close }) => (
				<MenuBody
					open={open}
					close={close}
					containerRef={containerRef}
					itemsRef={itemsRef}
					trigger={trigger}
					disabled={disabled}
					modal={modal}
					anchor={anchor}
					size={size}
					className={className}
				>
					{children}
				</MenuBody>
			)}
		</HeadlessMenu>
	);
};
